/**
 * Upsell logo depois de adicionar um kit.
 *
 * Fluxo: o cliente adiciona o kit → em vez do carrinho gaveta abre uma modal com
 * 2 ou 3 peças em oferta → ele adiciona uma delas OU fecha a modal → aí sim o
 * carrinho abre.
 *
 * Como o drawer é segurado: no Horizon o `CartAddEvent` e o `CartUpdateEvent`
 * são o MESMO evento ('cart:update'), e o drawer com `auto-open` abre em
 * qualquer um deles. O tema já tem o `conso-cart-drawer-guard.js`, que embrulha
 * o `showDialog` e ignora a abertura enquanto `window.__consoSilentCartUpdate`
 * estiver ligada. Aqui a flag sobe no CLIQUE do add-to-cart e só desce depois
 * que o dispatch termina — o drawer não abre, a sacola re-renderiza normal, e a
 * modal entra no lugar. Vale pros dois caminhos de add: o nativo
 * (`product-form.js`) e o do `conso-kit-tenis.js` nos kits com tênis avulso.
 *
 * Cada card é configurado por um bloco `conso-kit-upsell` dentro da section do
 * kit, que imprime só um JSON. A modal é uma só na página — montada aqui —
 * porque a LP renderiza os nove kits ao mesmo tempo e nove modais no DOM seria
 * peso morto.
 *
 * A linha do upsell entra marcada com a property `_conso_upsell`, que aponta
 * pro kit que liberou a oferta: o id da variante nos kits bundle, o token
 * `_conso_kitm` nos kits montados (que não põem linha própria no carrinho). É
 * o que a function `upsell-kit` usa pra saber que aquela peça foi comprada
 * dentro da oferta e aplicar o percentual de `custom.upsell_percent`. Sem a
 * property — ou sem o kit correspondente no carrinho — a linha é uma compra
 * avulsa e paga o preço normal do site.
 */
(() => {
  if (window.__consoKitUpsell) return;
  window.__consoKitUpsell = true;

  const SEL_KIT = '.conso-kit';
  const SEL_ATC = 'button[type="submit"], .add-to-cart-button, .sticky-add-to-cart__button';
  const SEL_DADOS = 'script[type="application/json"][data-conso-upsell]';

  // Se o add falhar não vem 'cart:update' nenhum. Sem esse teto a flag de
  // silêncio ficaria ligada e o drawer nunca mais abriria sozinho na página.
  const TETO_ESPERA_MS = 8000;

  const rotas = () => (window.Theme && window.Theme.routes) || {};

  /** @type {{kit: Element, variante: string, qtd: number} | null} */
  let pendente = null;
  let expiracao = null;
  let modal = null;

  /* ---------------------------------------------------------------- dados */

  function upsellsDo(kit) {
    return Array.from(kit.querySelectorAll(SEL_DADOS))
      .map((el) => {
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          console.error('[conso-kit-upsell] JSON inválido no bloco', e);
          return null;
        }
      })
      .filter((d) => d && Array.isArray(d.variantes) && d.variantes.length);
  }

  /**
   * Tamanho que o cliente escolheu na N-ésima peça do kit.
   *
   * Kit montado: cada peça é um slot do `conso-kit-montado`, na ordem em que
   * aparecem (o brinde é o último). Kit bundle: cada peça é uma opção do
   * produto, e o picker do Horizon marca cada fieldset com
   * data-fieldset-index = posição-1.
   */
  function tamanhoDoKit(kit, posicao) {
    if (!posicao) return '';

    const slots = kit.querySelectorAll('conso-kit-montado [data-km-slot]');
    if (slots.length) {
      const slot = slots[posicao - 1];
      const escolhido = slot && slot.querySelector('[data-km-tam]:checked');
      return escolhido ? escolhido.value : '';
    }

    const picker = kit.querySelector('variant-picker');
    if (!picker) return '';

    const indice = posicao - 1;

    const radio = picker.querySelector(`fieldset[data-fieldset-index="${indice}"] input[type="radio"]:checked`);
    if (radio) return radio.value;

    // Modo dropdown não imprime o data-fieldset-index; cai na ordem dos selects.
    const selects = picker.querySelectorAll('select.variant-option__select');
    return selects[indice] ? selects[indice].value : '';
  }

  const coresComEstoque = (dados) => {
    const disponiveis = new Set(dados.variantes.map((v) => v[1]));
    return dados.cores.filter((c) => disponiveis.has(c.nome));
  };

  const tamanhosDa = (dados, cor) =>
    dados.variantes.filter((v) => v[1] === cor).map((v) => v[2]);

  const varianteDe = (dados, cor, tamanho) => {
    const achado = dados.variantes.find((v) => v[1] === cor && v[2] === tamanho);
    return achado ? achado[0] : null;
  };

  /* -------------------------------------------------------------- carrinho */

  const idsDeSecaoDoCarrinho = () =>
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  /** A resposta de erro do /cart/add muda de forma conforme o pedido — ver conso-kit-tenis.js. */
  const mensagemDeErro = (dados) => {
    const errors = dados && dados.errors;

    if (typeof errors === 'string' && errors.trim()) return errors;

    if (errors && typeof errors === 'object') {
      const primeira = Object.values(errors)
        .flat()
        .find((m) => typeof m === 'string' && m.trim());
      if (primeira) return primeira;
    }

    return (dados && (dados.description || dados.message)) || 'Não foi possível adicionar ao carrinho.';
  };

  async function postCarrinho(itens) {
    const corpo = { items: itens };
    const secoes = idsDeSecaoDoCarrinho();
    if (secoes.length) {
      corpo.sections = secoes.join(',');
      corpo.sections_url = window.location.pathname;
    }

    const resposta = await fetch(rotas().cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(corpo),
    });
    const dados = await resposta.json();
    if (!resposta.ok || dados.status) throw new Error(mensagemDeErro(dados));
    return dados;
  }

  async function carrinhoAtual() {
    const r = await fetch(`${rotas().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return r.json();
  }

  /**
   * Roda `fn` com o drawer impedido de abrir sozinho. O dispatch de
   * 'cart:update' é síncrono, então a flag só precisa durar a chamada.
   */
  function silenciar(fn) {
    const anterior = window.__consoSilentCartUpdate;
    window.__consoSilentCartUpdate = true;
    try {
      fn();
    } finally {
      window.__consoSilentCartUpdate = anterior;
    }
  }

  function avisarTema(carrinho, sections) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: carrinho,
          sourceId: 'conso-kit-upsell',
          data: {
            itemCount: Number((carrinho && carrinho.item_count) || 0),
            source: 'conso-kit-upsell',
            sections,
          },
        },
      })
    );
  }

  function abrirSacola() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    // Sem drawer na página (tema sem gaveta): manda pro carrinho mesmo.
    const gatilho = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (gatilho) gatilho.click();
  }

  /* -------------------------------------------------------------- tracking */

  // `view_item_list` fica no gtag: o pixel nativo só dispara esse evento em
  // coleção, nunca em página, então aqui não há com o que competir.
  function rastrear(evento, payload) {
    if (typeof window.gtag === 'function') window.gtag('event', evento, payload);
  }

  // Eventos de nome próprio vão pelo dataLayer, junto com os `kit_*` do
  // conso-kit-tracking.js — assim o operador ajusta parâmetro no GTM sem push.
  function rastrearDL(payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // Fechou a modal tendo aceitado o upsell? Distingue recusa de conversão no
  // `kit_upsell_skip`, que fecha a taxa de aceite junto com o `view_item_list`.
  // `via` diz por onde saiu — recusar no botão não é o mesmo que fechar no X.
  let aceitou = false;
  let via = 'esc';

  /* ------------------------------------------------------------------ modal */

  function montarModal() {
    if (modal) return modal;

    modal = document.createElement('dialog');
    modal.className = 'conso-upsell';
    modal.setAttribute('aria-labelledby', 'conso-upsell-titulo');

    const caixa = document.createElement('div');
    caixa.className = 'conso-upsell__caixa';

    const cabecalho = document.createElement('header');
    cabecalho.className = 'conso-upsell__cabecalho';

    const titulo = document.createElement('h2');
    titulo.className = 'conso-upsell__titulo';
    titulo.id = 'conso-upsell-titulo';
    titulo.textContent = 'Kit adicionado. Aproveite e complete o look';

    const sub = document.createElement('p');
    sub.className = 'conso-upsell__sub';
    sub.textContent = 'Ofertas que só valem agora, junto com o seu kit.';

    const fechar = document.createElement('button');
    fechar.type = 'button';
    fechar.className = 'conso-upsell__fechar';
    fechar.setAttribute('aria-label', 'Fechar e ir para o carrinho');
    fechar.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>';
    fechar.addEventListener('click', () => {
      via = 'x';
      modal.close();
    });

    cabecalho.append(titulo, sub, fechar);

    const lista = document.createElement('div');
    lista.className = 'conso-upsell__lista';

    const rodape = document.createElement('footer');
    rodape.className = 'conso-upsell__rodape';

    const pular = document.createElement('button');
    pular.type = 'button';
    pular.className = 'conso-upsell__pular';
    pular.textContent = 'Não, obrigado — ir para o carrinho';
    pular.addEventListener('click', () => {
      via = 'botao';
      modal.close();
    });

    rodape.appendChild(pular);
    caixa.append(cabecalho, lista, rodape);
    modal.appendChild(caixa);

    // Clique no backdrop fecha (o <dialog> ocupa a tela toda; a caixa é filha).
    modal.addEventListener('click', (e) => {
      if (e.target !== modal) return;
      via = 'backdrop';
      modal.close();
    });

    // Fechar por qualquer via (X, ESC, backdrop, "não obrigado") leva à sacola.
    modal.addEventListener('close', () => {
      // `via` fica em 'esc' quando nenhum handler acima carimbou: é a única
      // saída que o <dialog> fecha sozinho.
      if (!aceitou) rastrearDL({ event: 'kit_upsell_skip', kit_upsell_via: via });

      abrirSacola();
    });

    document.body.appendChild(modal);
    return modal;
  }

  function montarCard(dados, contexto) {
    const cores = coresComEstoque(dados);
    if (!cores.length) return null;

    const corInicial =
      cores.find((c) => c.nome.toLowerCase() === String(dados.corPadrao || '').trim().toLowerCase()) || cores[0];

    const estado = { cor: corInicial.nome, tamanho: '' };

    const card = document.createElement('article');
    card.className = 'conso-upsell-card';

    /* mídia */
    const midia = document.createElement('div');
    midia.className = 'conso-upsell-card__midia';
    const foto = document.createElement('img');
    foto.loading = 'lazy';
    foto.alt = dados.titulo;
    foto.width = 200;
    foto.height = 260;
    midia.appendChild(foto);

    /* corpo */
    const corpo = document.createElement('div');
    corpo.className = 'conso-upsell-card__corpo';

    if (dados.rotulo) {
      const rotulo = document.createElement('p');
      rotulo.className = 'conso-upsell-card__rotulo';
      rotulo.textContent = dados.rotulo;
      corpo.appendChild(rotulo);
    }

    const nome = document.createElement('h3');
    nome.className = 'conso-upsell-card__titulo';
    nome.textContent = dados.titulo;
    corpo.appendChild(nome);

    const precos = document.createElement('p');
    precos.className = 'conso-upsell-card__precos';
    if (dados.percent > 0) {
      const de = document.createElement('s');
      de.textContent = dados.de;
      const por = document.createElement('strong');
      por.textContent = dados.por;
      const selo = document.createElement('span');
      selo.className = 'conso-upsell-card__selo';
      selo.textContent = `-${dados.percent}%`;
      precos.append(de, por, selo);
    } else {
      const por = document.createElement('strong');
      por.textContent = dados.de;
      precos.appendChild(por);
    }
    corpo.appendChild(precos);

    /* cores */
    const blocoCor = document.createElement('div');
    blocoCor.className = 'conso-upsell-card__opcao';
    const legendaCor = document.createElement('p');
    legendaCor.className = 'conso-upsell-card__legenda';
    const valorCor = document.createElement('span');
    legendaCor.append(`${dados.rotuloCor}: `, valorCor);
    const faixaCor = document.createElement('div');
    faixaCor.className = 'conso-upsell-card__swatches';
    faixaCor.setAttribute('role', 'group');
    faixaCor.setAttribute('aria-label', dados.rotuloCor);
    blocoCor.append(legendaCor, faixaCor);
    corpo.appendChild(blocoCor);

    /* tamanhos */
    const blocoTam = document.createElement('div');
    blocoTam.className = 'conso-upsell-card__opcao';
    const legendaTam = document.createElement('p');
    legendaTam.className = 'conso-upsell-card__legenda';
    legendaTam.textContent = dados.rotuloTamanho;
    const faixaTam = document.createElement('div');
    faixaTam.className = 'conso-upsell-card__pills';
    faixaTam.setAttribute('role', 'group');
    faixaTam.setAttribute('aria-label', dados.rotuloTamanho);
    blocoTam.append(legendaTam, faixaTam);
    corpo.appendChild(blocoTam);

    /* ação */
    const botao = document.createElement('button');
    botao.type = 'button';
    botao.className = 'conso-upsell-card__botao button';
    botao.textContent = 'Adicionar';
    corpo.appendChild(botao);

    const erro = document.createElement('p');
    erro.className = 'conso-upsell-card__erro';
    erro.hidden = true;
    corpo.appendChild(erro);

    card.append(midia, corpo);

    /* -------- render reativo -------- */

    const herdado = tamanhoDoKit(contexto.kit, dados.herdar);

    function desenharCores() {
      faixaCor.textContent = '';
      cores.forEach((cor) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'conso-upsell-card__swatch';
        b.title = cor.nome;
        b.setAttribute('aria-label', cor.nome);
        b.setAttribute('aria-pressed', String(cor.nome === estado.cor));
        if (cor.swatch) b.style.setProperty('--conso-swatch', cor.swatch);
        else b.classList.add('conso-upsell-card__swatch--sem');
        b.addEventListener('click', () => {
          if (estado.cor === cor.nome) return;
          estado.cor = cor.nome;
          estado.tamanho = '';
          desenhar();
        });
        faixaCor.appendChild(b);
      });
      valorCor.textContent = estado.cor;
    }

    function desenharTamanhos() {
      const tamanhos = tamanhosDa(dados, estado.cor);

      if (!estado.tamanho) {
        estado.tamanho = tamanhos.includes(herdado) ? herdado : '';
      }

      faixaTam.textContent = '';
      // Produto sem opção de tamanho: não desenha um seletor de nada.
      blocoTam.hidden = tamanhos.every((t) => !t);

      tamanhos.forEach((tamanho) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'conso-upsell-card__pill';
        b.textContent = tamanho;
        b.setAttribute('aria-pressed', String(tamanho === estado.tamanho));
        b.addEventListener('click', () => {
          estado.tamanho = tamanho;
          erro.hidden = true;
          desenhar();
        });
        faixaTam.appendChild(b);
      });
    }

    function desenharFoto() {
      const cor = cores.find((c) => c.nome === estado.cor);
      if (cor && cor.img) foto.src = cor.img;
    }

    function desenhar() {
      desenharCores();
      desenharTamanhos();
      desenharFoto();
    }

    desenhar();

    botao.addEventListener('click', async () => {
      const id = varianteDe(dados, estado.cor, estado.tamanho);

      if (!id) {
        erro.textContent = `Escolha o ${dados.rotuloTamanho.toLowerCase()}.`;
        erro.hidden = false;
        return;
      }

      botao.disabled = true;
      botao.classList.add('atc-loading');
      erro.hidden = true;

      try {
        const resposta = await postCarrinho([
          {
            id: Number(id),
            quantity: contexto.qtd,
            properties: {
              // Pareia a peça com o kit que a liberou. É o que a function
              // `upsell-kit` lê pra aplicar o percentual no checkout.
              _conso_upsell: contexto.variante,
            },
          },
        ]);

        const carrinho = await carrinhoAtual();

        aceitou = true;

        // NÃO é `add_to_cart`. O pixel nativo já dispara o dele nesta mesma
        // adição — os dois juntos davam 383,84 de `value` numa venda de 143,94,
        // e dobravam a conversão no Ads. Nome próprio resolve e ainda guarda o
        // dado que o nativo não tem: o preço COM o desconto do upsell, que a
        // function só aplica no checkout (o nativo reporta os 239,90 cheios).
        // Tudo com prefixo `kit_`: nomes como `item_name` e `tab` colidiriam com
        // os padrão do GA4 na lista de definições personalizadas, e definição
        // registrada não se renomeia depois.
        rastrearDL({
          event: 'kit_upsell_added',
          currency: dados.moeda,
          kit_upsell_valor: dados.valor * contexto.qtd,
          kit_upsell_valor_cheio: dados.de || null,
          kit_peca_id: String(dados.produtoId),
          kit_peca: dados.titulo,
          kit_peca_variante: `${estado.cor} / ${estado.tamanho}`,
          kit_peca_qtd: contexto.qtd,
        });

        // Re-renderiza a sacola sem deixar o drawer abrir por baixo da modal;
        // quem abre é o handler de 'close' logo abaixo.
        silenciar(() => avisarTema(carrinho, resposta.sections));
        modal.close();
      } catch (e) {
        console.error('[conso-kit-upsell]', e);
        erro.textContent = e && e.message ? e.message : 'Não foi possível adicionar.';
        erro.hidden = false;
      } finally {
        botao.disabled = false;
        botao.classList.remove('atc-loading');
      }
    });

    return card;
  }

  function abrirModal(contexto) {
    const upsells = upsellsDo(contexto.kit);
    if (!upsells.length) {
      abrirSacola();
      return;
    }

    const el = montarModal();
    const lista = el.querySelector('.conso-upsell__lista');
    lista.textContent = '';

    let montados = 0;
    upsells.forEach((dados) => {
      const card = montarCard(dados, contexto);
      if (card) {
        lista.appendChild(card);
        montados += 1;
      }
    });

    // Todas as peças esgotadas: não vale prender o cliente numa modal vazia.
    if (!montados) {
      abrirSacola();
      return;
    }

    // Zerado a cada abertura: a modal é reaproveitada, e sem isto o segundo kit
    // da sessão herdaria o resultado do primeiro.
    aceitou = false;
    via = 'esc';

    el.showModal();

    rastrear('view_item_list', {
      item_list_name: 'Upsell do kit',
      items: upsells.map((d, i) => ({
        item_id: String(d.produtoId),
        item_name: d.titulo,
        index: i,
      })),
    });
  }

  /* ------------------------------------------------------------- interceptação */

  function desarmar() {
    if (!pendente) return;
    pendente = null;
    window.__consoSilentCartUpdate = false;
    clearTimeout(expiracao);
  }

  document.addEventListener(
    'click',
    (evento) => {
      const alvo = evento.target instanceof Element ? evento.target.closest(SEL_ATC) : null;
      if (!alvo || alvo.disabled) return;

      const kit = alvo.closest(SEL_KIT);
      if (!kit || !kit.querySelector(SEL_DADOS)) return;

      // Kit montado não tem product form: o produto da página é display-only e
      // quem vai pro carrinho são as peças. O que identifica o kit é o token
      // que o bloco carimba em todas as linhas — é ele que vai na property
      // `_conso_upsell` no lugar do id da variante do bundle, e é por ele que a
      // function `upsell-kit` pareia a peça ofertada com o kit comprado.
      const montado = kit.querySelector('conso-kit-montado');
      const form = kit.querySelector('product-form-component form') || kit.querySelector('form[action*="/cart"]');

      let referencia = '';
      let qtd = 1;

      if (montado) {
        referencia = montado.dataset.kmToken || '';
      } else if (form && form.querySelector('[name="id"]')) {
        referencia = form.querySelector('[name="id"]').value;

        const bruta = Number(form.querySelector('[name="quantity"]') ? form.querySelector('[name="quantity"]').value : 1);
        qtd = Number.isFinite(bruta) && bruta > 0 ? Math.floor(bruta) : 1;
      }

      if (!referencia) return;

      pendente = { kit, variante: referencia, qtd };
      window.__consoSilentCartUpdate = true;

      clearTimeout(expiracao);
      expiracao = setTimeout(desarmar, TETO_ESPERA_MS);
    },
    true
  );

  document.addEventListener('cart:update', (evento) => {
    if (!pendente) return;

    // O add falhou: o tema dispara o mesmo evento marcado com didError. Sem
    // kit no carrinho não há upsell — só devolve o drawer ao normal.
    if (evento.detail && evento.detail.data && evento.detail.data.didError) {
      desarmar();
      return;
    }

    const contexto = pendente;
    pendente = null;
    clearTimeout(expiracao);

    // O dispatch é síncrono: só depois que todos os listeners rodarem (o do
    // drawer inclusive) é seguro devolver a flag.
    setTimeout(() => {
      window.__consoSilentCartUpdate = false;
    }, 0);

    abrirModal(contexto);
  });

  document.addEventListener('cart:error', desarmar);
})();
